import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useParams } from 'react-router';
import { TabConfigItem } from '../../@types';
import { QUERY_KEYS, SECOND } from '../../constants';
import useLocalization from '../useLocalization';
import useTabs from '../useTabs';
import { useCompetitions, usePublishedEvents, useSportById, useSports } from '../../queries';
import { getData } from '../../utils/api';

type TabValueOptions = 'matches' | 'outrights';

const DEFAULT_TAB_CONFIG: TabConfigItem<TabValueOptions>[] = [
  { label: 'matches', value: 'matches' },
  { label: 'outrights', value: 'outrights' },
];

type UseSportPageOptions = {
  tabConfig?: TabConfigItem<TabValueOptions>[];
};

export const useSportPage = (options: UseSportPageOptions = {}) => {
  const { tabConfig = DEFAULT_TAB_CONFIG } = options;
  const { sportId, section } = useParams();
  const { getLocalizedConfig, t } = useLocalization();

  const localizedTabs = useMemo(() => getLocalizedConfig(tabConfig), [getLocalizedConfig, tabConfig]);

  const defaultTab = section === 'outrights' ? 'outrights' : tabConfig[0].value;

  const tabProps = useTabs<TabValueOptions>({
    tabConfig: localizedTabs,
    defaultTab,
  });

  const { value } = tabProps;

  const EVENT_TYPE_MAP = {
    matches: { type: 'match' as const, message: t('noUpcomingMatchesForSport') },
    outrights: { type: 'outright' as const, message: t('noUpcomingOutrightsForSport') },
  };

  const eventType = EVENT_TYPE_MAP[value].type;
  const emptyMessage = EVENT_TYPE_MAP[value].message;

  const { data: sport } = useSportById(sportId);
  const { data: sportsData = [], isPending: isSportsLoading } = useSports({
    queryParams: {
      isLive: false,
    },
  });

  const { data: competitionsData, isPending: isLoadingCompetitions } = useCompetitions({
    sportId,
    type: eventType,
  });

  const { data: publishedEventListItemsData, isPending: isLoadingFeaturedEvents } = usePublishedEvents({
    queryKey: [QUERY_KEYS.featuredEvents, sportId],
    queryParams: {
      page: 1,
      limit: 50,
      sportId,
      featured: true,
      sort: 'startDate',
      count: false,
    },
    options: {
      enabled: !!sportId,
      refetchInterval: 30 * SECOND,
    },
  });

  const { data: upcomingEventsCount, isPending: isLoadingUpcomingEventsCount } = useQuery({
    queryKey: [QUERY_KEYS.upcomingMatchesCount, sportId],
    queryFn: () => getData('events/upcoming/count', { sportId }),
    enabled: !!sportId,
  });

  const { data: liveEventsCount } = useQuery({
    queryKey: [QUERY_KEYS.liveEventsCount, sportId],
    queryFn: (): Promise<{ count: number }> => getData('events/live/count', { sportId }),
    enabled: !!sportId,
  });

  const shouldShowFeaturedMatches = value !== 'outrights' && !!publishedEventListItemsData?.items.length;
  const shouldShowUpcomingMatches = value !== 'outrights' && !!upcomingEventsCount;

  return {
    sportId,
    sport,
    sportsData,
    isSportsLoading,
    tabProps,
    localizedTabs,
    eventType,
    emptyMessage,
    competitions: competitionsData?.items ?? [],
    isLoadingCompetitions,
    featuredEvents: publishedEventListItemsData?.items ?? [],
    topMarketTypes: publishedEventListItemsData?.topMarketTypes,
    isLoadingFeaturedEvents,
    upcomingEventsCount,
    isLoadingUpcomingEventsCount,
    liveEventsCount: liveEventsCount?.count,
    shouldShowFeaturedMatches,
    shouldShowUpcomingMatches,
  };
};

export { DEFAULT_TAB_CONFIG };
export type { TabValueOptions };
